/**
 * Desk — Inbox / Archive card list.
 * Untriaged cards (inbox) or archived cards, with text/email/date search.
 * Row click opens a right-side card panel with the full timeline.
 * catalyst-ui-kit + Tailwind; Desk deliberately does NOT use Polaris.
 */
import { FormEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { Heading } from "catalyst-ui-kit/heading";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import { Text } from "catalyst-ui-kit/text";
import { Badge } from "catalyst-ui-kit/badge";
import {
  fetchDeskCards,
  fetchDeskProjects,
  fetchMyDeskMailbox,
  updateDeskCard,
  createDeskCard,
  addDeskDayPlanEntry,
  refreshDeskMail,
  readSelectedDeskMailbox,
  writeSelectedDeskMailbox,
} from "services/desk.service";
import DeskNav from "components/desk-nav/desk-nav";
import {
  DeskCardListItem,
  DeskCardsFiltersValue,
  DeskClientWithProjects,
  DeskMailboxConfig,
} from "types/Desk";
import DeskCardDetailPanel from "components/desk-card-detail-panel/desk-card-detail-panel";
import DeskLinkCrmProjectPopover from "components/desk-link-crm-project-popover/desk-link-crm-project-popover";
import { REVEAL_ON_HOVER_CLS } from "components/desk-row-actions/reveal-on-hover";
import DateRangePicker from "features/datepicker/date-range-picker";
import useIsPhone from "features/files/use-is-phone";
import { formatPrettyDate } from "utils/helperFunctions";

const REFRESH_INTERVAL_MS = 30 * 1000;
/** Bulk mutations run a few at a time — enough to be quick, gentle on the API. */
const BULK_CONCURRENCY = 4;

function waitingAge(epochMs: number | null): string {
  if (!epochMs) return "";
  const mins = Math.floor((Date.now() - epochMs) / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

const vilniusDay = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Europe/Vilnius",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * List column stamp: the full 19-char date never fit the narrow column, so show
 * the time for today, day+month inside the current year and the date beyond it.
 */
function formatDeskTimestamp(epochMs: number | null | undefined, locale: string): string {
  if (!epochMs) return "-";
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return "-";
  const day = vilniusDay.format(date);
  const today = vilniusDay.format(new Date());
  const opts: Intl.DateTimeFormatOptions = { timeZone: "Europe/Vilnius" };
  if (day === today) {
    return new Intl.DateTimeFormat(locale, { ...opts, hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
  }
  if (day.slice(0, 4) === today.slice(0, 4)) {
    return new Intl.DateTimeFormat(locale, { ...opts, day: "2-digit", month: "short" }).format(date);
  }
  return formatPrettyDate(epochMs, locale);
}

/**
 * Sharees can only read (DeskService.canMutateCard). The backend flags every
 * row; older payloads without the flag fall back to "anything but a share".
 */
function cardCanMutate(card: DeskCardListItem): boolean {
  return card.canMutate === undefined ? !card.isSharedWithMe : card.canMutate;
}

/**
 * Runs one mutation per card with a small concurrency window. Unlike a plain
 * loop it never abandons the rest of the selection on the first failure — the
 * caller reports how many actually went through.
 */
async function runBulk<T>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
  onProgress: (done: number) => void
): Promise<{ ok: number; failed: number }> {
  let cursor = 0;
  let done = 0;
  let ok = 0;
  let failed = 0;
  const worker = async () => {
    while (cursor < items.length) {
      const item = items[cursor];
      cursor += 1;
      try {
        await fn(item);
        ok += 1;
      } catch {
        failed += 1;
      }
      done += 1;
      onProgress(done);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(BULK_CONCURRENCY, items.length) }, () => worker())
  );
  return { ok, failed };
}

const STATUS_COLOR: Record<string, "lime" | "amber" | "zinc"> = {
  open: "lime",
  waiting_client: "amber",
  done: "zinc",
};

/**
 * One action of the list toolbar. On a desktop it is an icon with a tooltip
 * (which also names its keyboard shortcut). Below 1024px the toolbar is a bar
 * pinned to the bottom of the screen and each button shows a caption under its
 * icon, because a touch screen has no tooltip. The caption is the button's name
 * everywhere (voice control says what it sees); the longer `label` is the tooltip.
 */
function BulkActionButton({
  label,
  caption,
  shortcut,
  danger,
  expanded,
  disabled,
  onClick,
  children,
}: {
  label: string;
  caption: string;
  shortcut?: string;
  danger?: boolean;
  expanded?: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-expanded={expanded}
      className={clsx(
        "rounded p-1.5 transition-colors hover:bg-zinc-200 dark:hover:bg-zinc-800",
        "max-lg:flex max-lg:min-h-14 max-lg:grow max-lg:basis-[22%] max-lg:flex-col max-lg:items-center max-lg:justify-center max-lg:gap-0.5 max-lg:rounded-lg max-lg:px-1 max-lg:py-1 md:max-lg:basis-[11%]",
        danger && "hover:text-red-500"
      )}
    >
      {children}
      <span className="line-clamp-2 text-center text-[11px] leading-tight lg:sr-only">{caption}</span>
    </button>
  );
}

export type DeskListMode = "inbox" | "archive" | "drafts" | "trash" | "shared";

interface Props {
  mode?: DeskListMode;
}

/** Archive tab — same list UI in archive mode. */
export function DeskArchive() {
  return <DeskInbox mode="archive" />;
}

export default function DeskInbox({ mode = "inbox" }: Props) {
  const isArchive = mode === "archive";
  const isDrafts = mode === "drafts";
  const isTrash = mode === "trash";
  const isShared = mode === "shared";
  // Which bulk actions make sense here — in Trash and Drafts most of them were
  // invisible no-ops, and a share recipient may not mutate anything at all.
  const canTriage = !isTrash && !isDrafts && !isShared;
  const canPlan = canTriage && !isArchive;
  const showToolbar = !isShared;
  const showViewSelect = canTriage && !isArchive;
  const dispatch = useDispatch();
  const isPhone = useIsPhone();
  const { companyId } = useParams<{ companyId: string }>();
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n as any).language === "lt" ? "lt-LT" : "en-GB";
  // Single-letter shortcuts must not fire behind a confirm dialog.
  const isModalOpen = useSelector((s: any) => Boolean(s.modalxReducer?.isOpen));
  const [searchParams, setSearchParams] = useSearchParams();
  const [cards, setCards] = useState<DeskCardListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // Page, view and the applied filters live in the URL: a reload or a link
  // lands on the same list, and switching tabs starts clean on page 1 because
  // the nav links carry no query string.
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  // "inbox" | "all" | a project id — ignored in archive mode
  const view = searchParams.get("view") || "inbox";
  const includeNoise = searchParams.get("noise") === "1";
  // Closed cards are history: the project view hides them by default so the
  // sidebar count matches the list, and this brings them back.
  const includeDone = searchParams.get("done") === "1";
  const appliedText = searchParams.get("q") || "";
  const appliedEmail = searchParams.get("qe") || "";
  const appliedFrom = searchParams.get("df") || "";
  const appliedTo = searchParams.get("dt") || "";
  const cardParam = searchParams.get("card");
  const activeCardNumber = cardParam && /^\d+$/.test(cardParam) ? Number(cardParam) : null;
  // Shared tab: all | with_me (shared with me) | by_me (I shared with others).
  const shareScopeRaw = searchParams.get("share");
  const shareScope: "all" | "with_me" | "by_me" =
    shareScopeRaw === "with_me" || shareScopeRaw === "by_me" ? shareScopeRaw : "all";
  const [searchText, setSearchText] = useState(appliedText);
  const [searchEmail, setSearchEmail] = useState(appliedEmail);
  const [dateRange, setDateRange] = useState({ start: appliedFrom, end: appliedTo });
  const loadSeq = useRef(0);
  const mailboxLoadSeq = useRef(0);
  // In-flight star overrides so polls / reloads don't clobber optimistic UI.
  const pendingStars = useRef(new Map<number, boolean>());
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isCheckingMail, setIsCheckingMail] = useState(false);
  const [busyCard, setBusyCard] = useState<number | null>(null);
  const [selectedNumbers, setSelectedNumbers] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [projectsTree, setProjectsTree] = useState<DeskClientWithProjects[]>([]);
  const [mailboxes, setMailboxes] = useState<DeskMailboxConfig[]>([]);
  const [mailboxId, setMailboxId] = useState<string | null>(null);
  const [mailboxesReady, setMailboxesReady] = useState(false);
  const [mailboxLoadError, setMailboxLoadError] = useState<string | null>(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [createTitle, setCreateTitle] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createName, setCreateName] = useState("");
  const [createNote, setCreateNote] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isProjectMenuOpen, setIsProjectMenuOpen] = useState(false);
  const projectMenuRef = useRef<HTMLDivElement | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  /** Merge into the query string without dropping the other params (?card=…). */
  const patchParams = useCallback(
    (updates: Record<string, string | null>, options?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === "") next.delete(key);
            else next.set(key, value);
          });
          return next;
        },
        { replace: options?.replace !== false }
      );
    },
    [setSearchParams]
  );

  const goToPage = useCallback(
    (next: number) => patchParams({ page: next > 1 ? String(next) : null }),
    [patchParams]
  );

  const loadMailboxes = useCallback(() => {
    if (!companyId) return;
    const seq = ++mailboxLoadSeq.current;
    setMailboxLoadError(null);
    fetchMyDeskMailbox(companyId)
      .then(({ mailboxes: list }) => {
        if (seq !== mailboxLoadSeq.current) return;
        setMailboxes(list);
        setMailboxLoadError(null);
        const stored = readSelectedDeskMailbox(companyId);
        // An empty stored value is a deliberate choice — the combined view.
        const pick =
          stored !== null && (stored === "" || list.some((m) => m.id === stored))
            ? stored
            : list[0]?.id || "";
        setMailboxId(pick || null);
        // The nav badges read the same value, so persist the resolved default
        // too — otherwise they count every mailbox while the list shows one.
        if (stored === null && pick) { writeSelectedDeskMailbox(companyId, pick); }
      })
      .catch((e: any) => {
        if (seq !== mailboxLoadSeq.current) return;
        setMailboxes([]);
        setMailboxId(null);
        setMailboxLoadError(e?.response?.data?.message || e?.message || t("desk_mailboxes_load_failed"));
      })
      .finally(() => {
        if (seq === mailboxLoadSeq.current) setMailboxesReady(true);
      });
  }, [companyId, t]);

  useEffect(() => {
    setMailboxesReady(false);
    loadMailboxes();
  }, [loadMailboxes]);

  const selectMailbox = (id: string) => {
    setMailboxId(id || null);
    goToPage(1);
    setSelectedNumbers(new Set());
    if (companyId) writeSelectedDeskMailbox(companyId, id);
  };

  const load = useCallback(async () => {
    if (!companyId || !mailboxesReady) return;
    const seq = ++loadSeq.current;
    try {
      let filtersValue: DeskCardsFiltersValue;
      // mailboxId is optional — without a connected mailbox, shared/assigned cards still load.
      // Shared tab ignores mailbox filter (shares are not mailbox-scoped).
      const mailboxFilter = isShared ? undefined : mailboxId || undefined;
      if (isArchive) {
        filtersValue = { view: "archive", mailboxId: mailboxFilter };
      } else if (isShared) {
        filtersValue = { view: "shared", shareScope };
      } else if (isDrafts) {
        filtersValue = { view: "drafts", mailboxId: mailboxFilter };
      } else if (isTrash) {
        filtersValue = { view: "trash", mailboxId: mailboxFilter };
      } else if (view === "inbox") {
        filtersValue = { view: "inbox", includeNoise, mailboxId: mailboxFilter };
      } else if (view === "all") {
        filtersValue = { view: "all", mailboxId: mailboxFilter };
      } else {
        filtersValue = { view: "project", projectId: view, includeDone, mailboxId: mailboxFilter };
      }

      if (appliedText) filtersValue.text = appliedText;
      if (appliedEmail) filtersValue.email = appliedEmail;
      if (appliedFrom) filtersValue.dateFrom = appliedFrom;
      if (appliedTo) filtersValue.dateTo = appliedTo;

      const data = await fetchDeskCards(companyId, {
        page,
        pageSize: 50,
        filtersValue,
      });
      if (seq !== loadSeq.current) return;
      // Merge pending star mutations so a concurrent poll can't revert them.
      const entries = data.entries.map((c) => {
        const pending = pendingStars.current.get(c.number);
        if (pending === undefined) return c;
        if (c.isStarred === pending) {
          pendingStars.current.delete(c.number);
          return c;
        }
        return { ...c, isStarred: pending };
      });
      setCards(entries);
      setTotal(data.pagination.total);
      setTotalPages(data.pagination.totalPages || 1);
      setLoadError(null);
      // Drop selection for cards that disappeared after refresh/delete.
      setSelectedNumbers((prev) => {
        if (!prev.size) return prev;
        const visible = new Set(entries.map((c) => c.number));
        const next = new Set(Array.from(prev).filter((n) => visible.has(n)));
        return next.size === prev.size ? prev : next;
      });
    } catch (e: any) {
      if (seq !== loadSeq.current) return;
      setLoadError(e?.response?.data?.message || e?.message || "error");
    } finally {
      if (seq === loadSeq.current) setIsLoading(false);
    }
  }, [
    companyId,
    includeNoise,
    includeDone,
    view,
    page,
    isArchive,
    isDrafts,
    isTrash,
    isShared,
    shareScope,
    appliedText,
    appliedEmail,
    appliedFrom,
    appliedTo,
    mailboxId,
    mailboxesReady,
  ]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  /** Always the current view's loader, for callers that await something slow
   *  first (checkMail) and must not reload the query the user has left. */
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    if (!companyId) return;
    fetchDeskProjects(companyId)
      .then((data) => setProjectsTree(data.clients || []))
      .catch(() => setProjectsTree([]));
  }, [companyId]);

  // Deleting the last page's cards leaves the pager pointing past the end.
  // Only ever act on a totalPages a load has actually reported: it starts at 1,
  // so clamping while loading would rewrite ?page=3 to 1 on every deep link.
  useEffect(() => {
    if (!isLoading && page > totalPages) goToPage(totalPages);
  }, [page, totalPages, goToPage, isLoading]);

  // Clear selection when page/view/mailbox changes.
  useEffect(() => {
    setSelectedNumbers(new Set());
  }, [page, view, includeNoise, includeDone, appliedText, appliedEmail, appliedFrom, appliedTo, mailboxId, isDrafts, isTrash, isShared]);

  // The filter drafts follow the URL — a tab switch or Back must not leave
  // typed text behind that no longer matches the list.
  useEffect(() => {
    setSearchText(appliedText);
    setSearchEmail(appliedEmail);
    setDateRange({ start: appliedFrom, end: appliedTo });
  }, [appliedText, appliedEmail, appliedFrom, appliedTo]);

  useEffect(() => {
    setIsLoading(true);
  }, [mailboxId, isArchive, isDrafts, isTrash, isShared, shareScope, view, page]);

  const openCard = (cardNumber: number) => {
    // Pushed, not replaced: Back closes the card instead of leaving Desk.
    patchParams({ card: String(cardNumber) }, { replace: false });
  };

  /**
   * Follow a card the panel moved to itself (a merge). Replaces rather than
   * pushes: the source card no longer exists as its own conversation, so a
   * Back that returned to it would reload something that is gone.
   */
  const followCard = (cardNumber: number) => {
    patchParams({ card: String(cardNumber) }, { replace: true });
  };

  const closeCreateTask = () => {
    setShowCreateTask(false);
    setCreateTitle("");
    setCreateEmail("");
    setCreateName("");
    setCreateNote("");
    setCreateError(null);
  };

  const submitCreateTask = async (e: FormEvent) => {
    if (e) e.preventDefault();
    if (!companyId || !mailboxId) return;
    const title = createTitle.trim();
    const customerEmail = createEmail.trim();
    if (!title) {
      setCreateError(t("desk_task_title_required"));
      return;
    }
    if (!customerEmail) {
      setCreateError(t("desk_customer_email_required"));
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    try {
      const { card } = await createDeskCard(companyId, {
        title,
        customerEmail,
        customerName: createName.trim() || undefined,
        text: createNote.trim() || undefined,
        mailboxId,
        isDraft: isDrafts,
      });
      closeCreateTask();
      await load();
      openCard(card.number);
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || err?.message || "error");
    } finally {
      setIsCreating(false);
    }
  };

  const closeCard = () => {
    patchParams({ card: null });
  };

  const applySearch = (e?: FormEvent) => {
    if (e) e.preventDefault();
    patchParams({
      page: null,
      q: searchText.trim(),
      qe: searchEmail.trim(),
      df: dateRange.start,
      dt: dateRange.end,
    });
  };

  const clearSearch = () => {
    patchParams({ page: null, q: null, qe: null, df: null, dt: null });
  };

  const toggleStar = async (card: DeskCardListItem) => {
    if (!companyId) return;
    const nextStarred = !card.isStarred;
    setBusyCard(card.number);
    // Optimistic UI so the star feels instant; keep until a reload confirms it.
    pendingStars.current.set(card.number, nextStarred);
    setCards((prev) =>
      prev.map((c) => (c.number === card.number ? { ...c, isStarred: nextStarred } : c))
    );
    try {
      await updateDeskCard(companyId, card.number, {
        isStarred: nextStarred,
      });
    } catch (e: any) {
      pendingStars.current.delete(card.number);
      setCards((prev) =>
        prev.map((c) => (c.number === card.number ? { ...c, isStarred: card.isStarred } : c))
      );
      toast.error(e?.response?.data?.message || e?.message || t("error"));
    } finally {
      setBusyCard(null);
    }
  };

  const openRowDelete = (cardNumber: number, cardTitle: string) => {
    if (!companyId) return;
    dispatch(setModalxComponent("DeleteDeskCardConfirm"));
    dispatch(setModalxOptions({ title: t("desk_confirm_delete_card_title") }));
    dispatch(
      setModalxComponentProps({
        companyId,
        cardNumber,
        cardTitle,
        isTrash,
        onConfirm: () => {
          setSelectedNumbers((prev) => {
            const next = new Set(prev);
            next.delete(cardNumber);
            return next;
          });
          load();
        },
      })
    );
    dispatch(setIsModalxOpen(true));
  };

  const openRestoreConfirm = (cardNumber: number, cardTitle: string, isDraft?: boolean) => {
    if (!companyId) return;
    dispatch(setModalxComponent("RestoreDeskCardConfirm"));
    dispatch(setModalxOptions({ title: t("desk_restore") }));
    dispatch(
      setModalxComponentProps({
        companyId,
        cardNumber,
        cardTitle,
        isDraft: Boolean(isDraft),
        onConfirm: () => {
          load();
        },
      })
    );
    dispatch(setIsModalxOpen(true));
  };

  const toggleSelected = (cardNumber: number, checked: boolean) => {
    setSelectedNumbers((prev) => {
      const next = new Set(prev);
      if (checked) next.add(cardNumber);
      else next.delete(cardNumber);
      return next;
    });
  };

  const allPageSelected = cards.length > 0 && cards.every((c) => selectedNumbers.has(c.number));
  const somePageSelected = cards.some((c) => selectedNumbers.has(c.number));

  const toggleSelectAllPage = (checked: boolean) => {
    setSelectedNumbers((prev) => {
      const next = new Set(prev);
      if (checked) {
        cards.forEach((c) => next.add(c.number));
      } else {
        cards.forEach((c) => next.delete(c.number));
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedNumbers(new Set());
    setIsProjectMenuOpen(false);
  };

  const openBulkDelete = () => {
    // Same filter as every other bulk action: a card shared with you read-only
    // would just answer 403 and turn the batch into a partial failure.
    const numbers = selectedMutable().map((c) => c.number);
    if (!companyId || !numbers.length) return;
    dispatch(setModalxComponent("DeleteDeskCardConfirm"));
    dispatch(setModalxOptions({ title: t("desk_confirm_delete_card_title") }));
    dispatch(
      setModalxComponentProps({
        companyId,
        cardNumbers: numbers,
        isTrash,
        onConfirm: () => {
          clearSelection();
          load();
        },
      })
    );
    dispatch(setIsModalxOpen(true));
  };

  /** Selected cards the current user is actually allowed to mutate. */
  const selectedMutable = useCallback(
    () => cards.filter((c) => selectedNumbers.has(c.number) && cardCanMutate(c)),
    [cards, selectedNumbers]
  );

  /**
   * Shared runner for every bulk action: keeps going past a failing card
   * instead of abandoning the rest of the selection, and reports the outcome
   * once — how many went through and how many did not.
   */
  const runBulkAction = useCallback(
    async (numbers: number[], fn: (cardNumber: number, cid: string) => Promise<unknown>) => {
      if (!companyId || !numbers.length) return;
      const cid = companyId;
      setBulkBusy(true);
      setBulkProgress({ done: 0, total: numbers.length });
      const { ok, failed } = await runBulk(
        numbers,
        (cardNumber) => fn(cardNumber, cid),
        (done) => setBulkProgress({ done, total: numbers.length })
      );
      clearSelection();
      // Always reload so partial success is reflected (and selection pruned).
      await load();
      setBulkProgress(null);
      setBulkBusy(false);
      if (!failed) toast.success(t("desk_bulk_success", { count: ok }));
      else if (ok) toast.warning(t("desk_bulk_partial", { success: ok, failed }));
      else toast.error(t("desk_bulk_failed", { count: failed }));
    },
    [companyId, load, t]
  );

  const bulkToggleArchive = () => {
    const selected = selectedMutable();
    // Archive mode → unarchive; inbox → archive.
    const archived = !isArchive;
    return runBulkAction(selected.map((c) => c.number), (cardNumber, cid) =>
      updateDeskCard(cid, cardNumber, { archived })
    );
  };

  const bulkToggleDone = useCallback(() => {
    const selected = selectedMutable();
    const hasUnresolved = selected.some((c) => c.status !== "done");
    const nextStatus = hasUnresolved ? "done" : "open";
    return runBulkAction(selected.map((c) => c.number), (cardNumber, cid) =>
      updateDeskCard(cid, cardNumber, { status: nextStatus })
    );
  }, [runBulkAction, selectedMutable]);

  const bulkToggleNoise = useCallback(() => {
    const selected = selectedMutable();
    const hasClean = selected.some((c) => !c.isNoise);
    return runBulkAction(selected.map((c) => c.number), (cardNumber, cid) =>
      updateDeskCard(cid, cardNumber, { isNoise: hasClean })
    );
  }, [runBulkAction, selectedMutable]);

  /** projectId null moves the cards back to the untriaged Inbox. */
  const bulkMoveToProject = (projectId: string | null) => {
    setIsProjectMenuOpen(false);
    return runBulkAction(selectedMutable().map((c) => c.number), (cardNumber, cid) =>
      updateDeskCard(cid, cardNumber, { project: projectId })
    );
  };

  const bulkAddToToday = useCallback(() => {
    const todayStr = new Date().toLocaleDateString("sv");
    return runBulkAction(selectedMutable().map((c) => c.number), (cardNumber, cid) =>
      addDeskDayPlanEntry(cid, todayStr, { cardNumber })
    );
  }, [runBulkAction, selectedMutable]);

  const bulkSnoozeTomorrow = useCallback(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    const snoozedUntil = d.getTime();
    return runBulkAction(selectedMutable().map((c) => c.number), (cardNumber, cid) =>
      updateDeskCard(cid, cardNumber, { snoozedUntil })
    );
  }, [runBulkAction, selectedMutable]);

  /**
   * Ask the mailroom to check the mailbox right now. The list already polls
   * every 30 seconds, but polling only re-reads cards that ingest has already
   * made — this is the only way to make the app go and LOOK at the mailbox.
   *
   * The reply says what happened per mailbox, so a mailbox that could not be
   * checked is named with its reason instead of disappearing into a success.
   */
  const checkMail = useCallback(async () => {
    if (!companyId || isCheckingMail) return;
    setIsCheckingMail(true);
    try {
      const { mailboxes: results } = await refreshDeskMail(companyId, mailboxId || undefined);
      const fetched = results.reduce((sum, r) => sum + (r.processed || 0), 0);
      // Anything but `swept` means that mailbox was not looked at, `busy`
      // included — the sweep already running there may be pulling mail in right
      // now, so "no new mail" is a claim we cannot make. It gets a calmer toast
      // than a real failure, not a silent pass.
      const unchecked = results.filter((r) => r.status !== "swept");
      unchecked.forEach((r) => {
        const line = t("desk_check_mail_problem", {
          address: r.address || "",
          reason: r.error || t(`desk_check_mail_status_${r.status}`),
        });
        if (r.status === "busy") { toast.info(line); } else { toast.warning(line); }
      });
      if (fetched > 0) {
        toast.success(t("desk_check_mail_new", { count: fetched }));
      } else if (!unchecked.length) {
        toast.success(t("desk_check_mail_none"));
      }
      // Reload even when nothing new arrived: a sweep that ran while the list
      // was open may have ingested mail this view has not shown yet.
      //
      // Through the ref, never the captured `load`: the wait can outlast the
      // view. Switching mailbox or page mid-check already loaded the new query,
      // and a stale closure firing afterwards would bump loadSeq and overwrite
      // the list with the query the user left behind.
      await loadRef.current();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || t("error"));
    } finally {
      setIsCheckingMail(false);
    }
  }, [companyId, isCheckingMail, mailboxId, t]);

  const bulkPublish = () => {
    return runBulkAction(selectedMutable().map((c) => c.number), (cardNumber, cid) =>
      updateDeskCard(cid, cardNumber, { isDraft: false })
    );
  };

  const bulkRestore = () => {
    return runBulkAction(selectedMutable().map((c) => c.number), (cardNumber, cid) =>
      updateDeskCard(cid, cardNumber, { deleted: false })
    );
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if modifier keys (Ctrl, Cmd, Alt) are pressed
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }

      // Ignore if bulk operations are currently running
      if (bulkBusy) {
        return;
      }

      // A confirm dialog or an open card owns the keyboard — Polaris keeps the
      // focus on a <button>, which the INPUT/TEXTAREA filter below never catches.
      if (isModalOpen || activeCardNumber !== null) {
        return;
      }

      const activeEl = document.activeElement;
      if (
        activeEl &&
        (activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          activeEl.tagName === "SELECT" ||
          (activeEl as HTMLElement).isContentEditable)
      ) {
        return;
      }

      if (!selectedNumbers.size) return;

      switch (e.key.toLowerCase()) {
        case "d":
          if (!canTriage) return;
          e.preventDefault();
          bulkToggleDone();
          break;
        case "n":
          if (!canTriage) return;
          e.preventDefault();
          bulkToggleNoise();
          break;
        case "t":
          if (!canPlan) return;
          e.preventDefault();
          bulkAddToToday();
          break;
        case "s":
          if (!canPlan) return;
          e.preventDefault();
          bulkSnoozeTomorrow();
          break;
        case "p":
          if (!canTriage) return;
          e.preventDefault();
          setIsProjectMenuOpen((prev) => !prev);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedNumbers,
    companyId,
    bulkBusy,
    isModalOpen,
    activeCardNumber,
    canTriage,
    canPlan,
    bulkToggleDone,
    bulkToggleNoise,
    bulkAddToToday,
    bulkSnoozeTomorrow,
  ]);

  // The menu is click-driven now: a hover menu opened and closed on a single
  // touch tap and collapsed when the cursor crossed the gap to it.
  useEffect(() => {
    if (!isProjectMenuOpen) return undefined;
    const first = projectMenuRef.current?.querySelector("[data-project-option]") as HTMLElement | null;
    if (first) first.focus();
    const onMouseDown = (e: MouseEvent) => {
      if (projectMenuRef.current && !projectMenuRef.current.contains(e.target as Node)) {
        setIsProjectMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsProjectMenuOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isProjectMenuOpen]);

  // On a phone fields are 16px (iOS zooms into anything smaller) and 44px tall; on any touch
  // screen 44px too, like the Polaris date range field next to them.
  const inputCls =
    "rounded-lg border border-zinc-300 bg-transparent px-3 py-1.5 text-sm outline-none transition-all duration-150 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 dark:focus:ring-indigo-400/20 dark:focus:border-indigo-400 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 max-md:text-base";

  const checkboxCls =
    "size-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600 cursor-pointer";

  const hasActiveSearch = !!(appliedText || appliedEmail || appliedFrom || appliedTo);
  const hasSearchDraft = !!(searchText || searchEmail || dateRange.start || dateRange.end);
  const isSearchDirty =
    searchText.trim() !== appliedText ||
    searchEmail.trim() !== appliedEmail ||
    dateRange.start !== appliedFrom ||
    dateRange.end !== appliedTo;
  const selectedCount = selectedNumbers.size;
  const projectName =
    projectsTree
      .flatMap((c) => c.projects)
      .find((p) => p.id === view)?.name || null;
  // The heading used to say "Inbox" while a project view was on screen.
  const viewLabel = !showViewSelect || view === "inbox"
    ? null
    : view === "all"
      ? t("desk_view_all")
      : projectName;

  return (
    <div className={clsx("mx-auto max-w-full p-4 md:p-6", showToolbar && selectedCount > 0 && "max-lg:pb-40")}>
      <DeskNav />
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-100 pb-4 dark:border-zinc-900">
        <Heading className="!font-bold">
          {t(
            isArchive
              ? "desk_archive"
              : isShared
                ? "desk_shared"
                : isDrafts
                  ? "desk_drafts"
                  : isTrash
                    ? "desk_trash"
                    : "desk_inbox"
          )}
          {viewLabel && <span className="text-zinc-500 font-normal"> · {viewLabel}</span>}{" "}
          {total > 0 && <span className="text-zinc-500 text-base font-normal">({total})</span>}
        </Heading>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {isShared && (
            <div className="flex flex-wrap items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-800 dark:bg-zinc-900/40">
              {(
                [
                  { id: "all", labelKey: "desk_shared_scope_all" },
                  { id: "with_me", labelKey: "desk_shared_scope_with_me" },
                  { id: "by_me", labelKey: "desk_shared_scope_by_me" },
                ] as const
              ).map((scope) => {
                const active = shareScope === scope.id;
                return (
                  <button
                    key={scope.id}
                    type="button"
                    onClick={() =>
                      patchParams({
                        share: scope.id === "all" ? null : scope.id,
                        page: null,
                      })
                    }
                    className={
                      "rounded-md px-2.5 py-1 text-xs font-semibold transition-colors max-md:min-h-11 max-md:px-3 max-md:text-sm " +
                      (active
                        ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100"
                        : "text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200")
                    }
                  >
                    {t(scope.labelKey)}
                  </button>
                );
              })}
            </div>
          )}
          {mailboxes.length > 0 && !isShared && (
            <label className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                {t("desk_mailbox_switch")}
              </span>
              <select
                value={mailboxId || ""}
                onChange={(e) => selectMailbox(e.target.value)}
                className={inputCls + " max-w-[260px] font-medium"}
              >
                {(mailboxes.length > 1 || !mailboxId) && (
                  <option value="">{t("desk_all_mailboxes")}</option>
                )}
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.address}
                    {!m.isActive ? ` (${t("desk_mailbox_disabled")})` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          {/* Shared cards come from other people's mailboxes — checking mine
              would not change that list, so the button stays out of it. */}
          {!isShared && mailboxes.some((m) => m.isActive) && (
            <button
              type="button"
              onClick={checkMail}
              disabled={isCheckingMail}
              title={t("desk_check_mail")}
              className="rounded-lg border border-zinc-300 px-3 py-1.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 active:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-900 max-md:min-h-11"
            >
              {isCheckingMail ? t("desk_check_mail_checking") : t("desk_check_mail")}
            </button>
          )}
          {!isArchive && !isTrash && !isShared && mailboxes.length > 0 && mailboxId && (
            <button
              type="button"
              onClick={() => {
                setShowCreateTask((v) => !v);
                setCreateError(null);
              }}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 active:bg-indigo-700 shadow-sm transition-colors max-md:min-h-11"
            >
              {t("desk_new_task")}
            </button>
          )}
          {showViewSelect && (
            <>
              <select
                value={view}
                onChange={(e) => patchParams({ view: e.target.value === "inbox" ? null : e.target.value, page: null })}
                className={inputCls + " max-w-[220px] font-medium"}
              >
                <option value="inbox">{t("desk_inbox")}</option>
                <option value="all">{t("desk_view_all")}</option>
                {projectsTree.map((client) => (
                  <optgroup key={client.id} label={client.name}>
                    {client.projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.activeCards ? ` (${p.activeCards})` : ""}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {/* Only Inbox filters noise — the other views list it either way,
                  so a checkbox there would change the URL and nothing else. */}
              {view === "inbox" && (
                <label className="flex cursor-pointer select-none items-center gap-2 text-zinc-700 dark:text-zinc-300 font-medium max-md:min-h-11">
                  <input
                    type="checkbox"
                    checked={includeNoise}
                    onChange={(e) => patchParams({ noise: e.target.checked ? "1" : null, page: null })}
                    className="size-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
                  />
                  {t("desk_show_noise")}
                </label>
              )}
              {/* Only inside a project: elsewhere done cards are already out of
                  scope, and the count beside the project name excludes them. */}
              {view !== "inbox" && view !== "all" && (
                <label className="flex cursor-pointer select-none items-center gap-2 text-zinc-700 dark:text-zinc-300 font-medium max-md:min-h-11">
                  <input
                    type="checkbox"
                    checked={includeDone}
                    onChange={(e) => patchParams({ done: e.target.checked ? "1" : null, page: null })}
                    className="size-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
                  />
                  {t("desk_show_done")}
                </label>
              )}
            </>
          )}
        </div>
      </div>

      {!isArchive && showCreateTask && mailboxId && (
        <form
          onSubmit={submitCreateTask}
          className="mt-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950"
        >
          <Heading level={3} className="!text-base !font-bold !mb-3">
            {t("desk_new_task_title")}
          </Heading>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500 sm:col-span-2">
              {t("desk_task_title")}
              <input
                type="text"
                value={createTitle}
                onChange={(e) => setCreateTitle(e.target.value)}
                placeholder={t("desk_task_title_placeholder")}
                className={inputCls}
                autoFocus={!isPhone}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
              {t("desk_customer_email")}
              <input
                type="email"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                className={inputCls}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
              {t("desk_customer_name")}
              <input
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder={t("desk_customer_name_optional")}
                className={inputCls}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500 sm:col-span-2">
              {t("desk_task_note_optional")}
              <textarea
                value={createNote}
                onChange={(e) => setCreateNote(e.target.value)}
                rows={3}
                className={inputCls}
              />
            </label>
          </div>
          {createError && (
            <div className="mt-3 rounded-xl bg-red-500/10 p-3 text-xs text-red-700 border border-red-500/20 dark:text-red-400">
              {createError}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={isCreating}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-40 max-md:min-h-11"
            >
              {isCreating ? t("desk_creating_task") : t("desk_create_task")}
            </button>
            <button
              type="button"
              onClick={closeCreateTask}
              disabled={isCreating}
              className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-900 max-md:min-h-11"
            >
              {t("cancel")}
            </button>
          </div>
        </form>
      )}

      {mailboxesReady && mailboxLoadError && (
        <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 p-3.5 flex flex-wrap items-center justify-between gap-3">
          <Text className="!text-red-700 dark:!text-red-400 font-medium">{mailboxLoadError}</Text>
          <button
            type="button"
            onClick={() => {
              setMailboxesReady(false);
              loadMailboxes();
            }}
            className="rounded-lg border border-red-300/50 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-500/10 dark:border-red-800 dark:text-red-300 max-md:min-h-11"
          >
            {t("retry")}
          </button>
        </div>
      )}

      {mailboxesReady && !mailboxLoadError && mailboxes.length === 0 && (
        <div className="mt-4 rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/80 p-4 text-center dark:border-zinc-700 dark:bg-zinc-900/30">
          <Text className="text-zinc-500 font-medium">{t("desk_no_mailboxes")}</Text>
          <Text className="!mt-1 !text-sm !text-zinc-400">{t("desk_shared_without_mailbox_hint")}</Text>
        </div>
      )}

      <form
        onSubmit={applySearch}
        className="mt-4 flex flex-wrap items-end gap-3 rounded-2xl border border-zinc-200/80 bg-zinc-50/80 p-4 dark:border-zinc-800 dark:bg-zinc-950/40"
      >
        <label className="flex w-full min-w-0 flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500 sm:w-auto sm:min-w-[160px] sm:flex-1">
          {t("desk_search_text")}
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t("desk_search_text_placeholder")}
            className={inputCls + " font-normal normal-case tracking-normal"}
          />
        </label>
        <label className="flex w-full min-w-0 flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500 sm:w-auto sm:min-w-[160px] sm:flex-1">
          {t("desk_search_email")}
          <input
            type="search"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder={t("desk_search_email_placeholder")}
            className={inputCls + " font-normal normal-case tracking-normal"}
          />
        </label>
        <div className="w-full sm:w-auto sm:min-w-[275px] sm:flex-1 max-md:[&_.Polaris-TextField\_\_Input]:min-h-11 [&_.Polaris-Label]:text-xs [&_.Polaris-Label]:font-semibold [&_.Polaris-Label]:uppercase [&_.Polaris-Label]:tracking-wider [&_.Polaris-Label]:text-zinc-500">
          <DateRangePicker
            start={dateRange.start}
            end={dateRange.end}
            onChange={(r) => setDateRange({ start: r.start, end: r.end })}
            label={t("date_range")}
          />
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          {/* Highlighted while the typed filters differ from the applied ones. */}
          <button
            type="submit"
            className={
              "rounded-lg px-3.5 py-1.5 text-sm font-semibold text-white max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 max-sm:flex-1 " +
              (isSearchDirty
                ? "bg-indigo-600 hover:bg-indigo-500"
                : "bg-zinc-900 hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white")
            }
          >
            {t("desk_search")}
          </button>
          {(hasActiveSearch || hasSearchDraft) && (
            <button
              type="button"
              onClick={clearSearch}
              className="rounded-lg border border-zinc-200 px-3.5 py-1.5 text-sm font-medium hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-900 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11"
            >
              {t("desk_search_clear")}
            </button>
          )}
        </div>
      </form>

      {loadError && (
        <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 p-3.5">
          <Text className="!text-red-700 dark:!text-red-400 font-medium">{loadError}</Text>
        </div>
      )}

      {isLoading && !cards.length && !loadError && (
        <div className="mt-5 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex animate-pulse items-center gap-3 border-b border-zinc-100 px-4 py-3.5 last:border-b-0 dark:border-zinc-900">
              <div className="size-4 rounded bg-zinc-200 dark:bg-zinc-800" />
              <div className="h-4 w-40 rounded bg-zinc-200 dark:bg-zinc-800" />
              <div className="h-4 flex-1 rounded bg-zinc-100 dark:bg-zinc-900" />
              <div className="h-4 w-20 rounded bg-zinc-100 dark:bg-zinc-900" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && !cards.length && !loadError && (
        <div className="mt-6 rounded-2xl border border-zinc-200/50 bg-zinc-50 p-12 text-center dark:border-zinc-800 dark:bg-zinc-900/20">
          <Text className="text-zinc-500 font-medium">
            {hasActiveSearch
              ? t("desk_search_empty")
              : t(
                  isArchive
                    ? "desk_archive_empty"
                    : isShared
                    ? shareScope === "by_me"
                      ? "desk_shared_by_me_empty"
                      : shareScope === "with_me"
                        ? "desk_shared_with_me_empty"
                        : "desk_shared_empty"
                    : isDrafts
                    ? "desk_drafts_empty"
                    : isTrash
                    ? "desk_trash_empty"
                    : "desk_inbox_empty"
                )}
          </Text>
        </div>
      )}

      {cards.length > 0 && (
        <div className="mt-5 border border-zinc-200 dark:border-zinc-800 rounded-2xl overflow-hidden bg-white dark:bg-zinc-950 shadow-sm">
          {/* Top Gmail-like Toolbar — only the actions this tab can actually perform.
              Below 1024px its buttons leave the header and become a bar pinned to the
              bottom of the screen, shown only while cards are selected. */}
          {showToolbar && (
          <div className="flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30 px-4 py-3 text-zinc-500 dark:text-zinc-400 max-md:py-1">
            <div className="flex items-center gap-1">
              <label className="flex items-center max-md:size-11 max-md:justify-center">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = somePageSelected && !allPageSelected;
                  }}
                  onChange={(e) => toggleSelectAllPage(e.target.checked)}
                  className={checkboxCls}
                  aria-label={t("desk_select_all_on_page")}
                />
              </label>
              <button
                type="button"
                onClick={() => toggleSelectAllPage(!allPageSelected)}
                className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded max-md:p-3.5"
                aria-label={t("desk_select_all_on_page")}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            <div className="h-4 w-px bg-zinc-200 dark:bg-zinc-800 mx-1"></div>

            <div
              className={clsx(
                "flex flex-wrap items-center gap-3 transition-opacity",
                "max-lg:fixed max-lg:inset-x-0 max-lg:bottom-[var(--phone-bar-height,0px)] max-lg:z-30 max-lg:gap-1 max-lg:border-t max-lg:border-zinc-200 max-lg:bg-white max-lg:p-2 max-lg:shadow-[0_-2px_8px_rgba(0,0,0,0.08)] dark:max-lg:border-zinc-800 dark:max-lg:bg-zinc-950",
                selectedCount > 0 ? "opacity-100" : "opacity-40 pointer-events-none max-lg:hidden"
              )}
            >
              {canTriage && (
                <>
                  {/* Envelope (Mark Done/Open) */}
                  <BulkActionButton
                    disabled={bulkBusy}
                    onClick={bulkToggleDone}
                    label={t("desk_mark_done_toggle")}
                    caption={t("desk_status_done")}
                    shortcut="d"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 19v-8.93a2 2 0 01.89-1.664l8-5.333a2 2 0 012.22 0l8 5.333A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-2.25-1.5a2 2 0 00-2.22 0l-2.25 1.5" />
                    </svg>
                  </BulkActionButton>

                  {/* Exclamation (Spam / Noise) */}
                  <BulkActionButton
                    disabled={bulkBusy}
                    onClick={bulkToggleNoise}
                    label={t("desk_toggle_noise")}
                    caption={t("desk_noise")}
                    shortcut="n"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </BulkActionButton>
                </>
              )}

              {canPlan && (
                <>
                  {/* Calendar (→ Today) */}
                  <BulkActionButton
                    disabled={bulkBusy}
                    onClick={bulkAddToToday}
                    label={t("desk_add_to_today")}
                    caption={t("desk_add_to_today")}
                    shortcut="t"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </BulkActionButton>

                  {/* Clock (Snooze → tomorrow) */}
                  <BulkActionButton
                    disabled={bulkBusy}
                    onClick={bulkSnoozeTomorrow}
                    label={t("desk_snooze_tomorrow")}
                    caption={t("desk_snooze_tomorrow")}
                    shortcut="s"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </BulkActionButton>
                </>
              )}

              {/* Trash (Delete) */}
              <BulkActionButton
                disabled={bulkBusy}
                onClick={openBulkDelete}
                label={t(isTrash ? "desk_delete_permanently" : "desk_confirm_delete_card_title")}
                caption={t("delete")}
                danger
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </BulkActionButton>

              {isTrash && (
                /* Restore selected cards out of the Trash */
                <BulkActionButton
                  disabled={bulkBusy}
                  onClick={bulkRestore}
                  label={t("desk_restore")}
                  caption={t("desk_restore")}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                </BulkActionButton>
              )}

              {isDrafts && (
                /* Publish drafts into the Inbox queue */
                <BulkActionButton
                  disabled={bulkBusy}
                  onClick={bulkPublish}
                  label={t("desk_publish")}
                  caption={t("desk_publish")}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                </BulkActionButton>
              )}

              {canTriage && (
                /* Folder (Move to project). On a desktop a dropdown under the button;
                   below 1024px a sheet over the bottom bar, so it cannot be cut off. */
                <div className="relative max-lg:contents" ref={projectMenuRef}>
                  <BulkActionButton
                    disabled={bulkBusy}
                    onClick={() => setIsProjectMenuOpen((prev) => !prev)}
                    label={t("desk_move_to_project")}
                    caption={t("desk_move_to_project")}
                    shortcut="p"
                    expanded={isProjectMenuOpen}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                  </BulkActionButton>
                  {isProjectMenuOpen && (
                    <div className="absolute left-0 mt-1 w-56 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-lg z-50 py-1 text-sm max-h-60 overflow-y-auto max-lg:fixed max-lg:inset-x-0 max-lg:bottom-[var(--phone-bar-height,0px)] max-lg:z-[60] max-lg:mt-0 max-lg:max-h-[70dvh] max-lg:w-auto max-lg:rounded-b-none">
                      <div className="px-3 py-1 text-xs font-semibold text-zinc-500 border-b border-zinc-100 dark:border-zinc-900 mb-1">
                        {t("desk_move_to_project")}
                      </div>
                      <button
                        type="button"
                        data-project-option
                        onClick={() => bulkMoveToProject(null)}
                        className="w-full text-left px-4 py-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 truncate max-lg:py-3"
                      >
                        {t("desk_no_project")}
                      </button>
                      {!projectsTree.some((client) => client.projects.length > 0) && (
                        <div className="px-4 py-2 text-xs text-zinc-500">{t("desk_no_projects")}</div>
                      )}
                      {projectsTree.map((client) => (
                        <div key={client.id}>
                          <div className="px-3 py-0.5 text-xs font-semibold text-zinc-500 truncate">{client.name}</div>
                          {client.projects.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              data-project-option
                              onClick={() => bulkMoveToProject(p.id)}
                              className="w-full text-left px-4 py-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 truncate max-lg:py-3"
                            >
                              {p.name}
                              {p.activeCards ? ` (${p.activeCards})` : ""}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {canTriage && (
                /* Archive (Tray) */
                <BulkActionButton
                  disabled={bulkBusy}
                  onClick={bulkToggleArchive}
                  label={t(isArchive ? "desk_unarchive" : "desk_archive_action")}
                  caption={t(isArchive ? "desk_unarchive" : "desk_archive_action")}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
                  </svg>
                </BulkActionButton>
              )}
            </div>

            <span className="ml-auto flex items-center gap-3 text-xs font-medium text-zinc-500">
              {bulkProgress && (
                <span>{`${bulkProgress.done} / ${bulkProgress.total}`}</span>
              )}
              {selectedCount > 0 && (
                <>
                  <span>{t("desk_selected_count", { count: selectedCount })}</span>
                  <span className="hidden lg:inline text-zinc-400">{t("desk_shortcuts_hint")}</span>
                </>
              )}
            </span>
          </div>
          )}

          {/* Clean List Rows */}
          <div
            className={`flex flex-col divide-y divide-zinc-100 dark:divide-zinc-900 ${
              isLoading ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            {cards.map((card) => {
              const isSelected = selectedNumbers.has(card.number);
              const rowBusy = busyCard === card.number || bulkBusy;
              const canMutate = cardCanMutate(card);
              const sender = card.contact?.name || card.contact?.email || t("desk_unknown_sender");
              // One flag for all three carriers of the state — stripe, weight
              // and badge — so they cannot contradict each other in one row.
              // Archive and Trash order by their own column, so a mark that
              // implies "act on this first" would be a lie there.
              const showReplied = Boolean(card.clientReplied) && !isArchive && !isTrash;
              const shownAt = isTrash
                ? card.deletedAt || card.createdAt
                : isArchive
                  ? card.archivedAt || card.createdAt
                  : card.lastMessageAt || card.createdAt;
              return (
                <div
                  key={card.id}
                  role="button"
                  tabIndex={0}
                  aria-label={
                    // role="button" makes the row's children presentational, so
                    // the badge alone would never reach a screen reader — and
                    // colour plus font weight is all a sighted user gets.
                    t("desk_open_card_aria", { contact: sender, title: card.title }) +
                    (showReplied ? `, ${t("desk_client_replied")}` : "")
                  }
                  onClick={() => openCard(card.number)}
                  onKeyDown={(e) => {
                    // Only the row itself — Enter on the star or the checkbox
                    // must not open the card as well.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openCard(card.number);
                    }
                  }}
                  className={`group relative flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 max-sm:py-2 cursor-pointer hover:bg-zinc-50/50 dark:hover:bg-zinc-900/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500/40 ${
                    // An inset bar rather than a left border: the list is
                    // rounded-2xl overflow-hidden, so a border on the first row
                    // would be sliced by the corner, and it would push every
                    // row's content 3px out of line with the toolbar above.
                    showReplied
                      ? "before:absolute before:inset-y-1.5 before:left-1 before:w-[3px] before:rounded-full before:bg-emerald-500"
                      : ""
                  } ${isSelected ? "bg-indigo-50/30 dark:bg-indigo-950/10" : ""}`}
                >
                  {showToolbar && (
                    // A label, so the padding around the 16px box is part of the tap target.
                    <label
                      className="flex items-center max-md:size-11 max-md:justify-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => toggleSelected(card.number, e.target.checked)}
                        className={checkboxCls}
                        aria-label={t("desk_open_card_aria", { contact: sender, title: card.title })}
                      />
                    </label>
                  )}

                  {canMutate && (
                    <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        disabled={rowBusy}
                        onClick={() => toggleStar(card)}
                        aria-label={t(card.isStarred ? "desk_unstar" : "desk_star")}
                        aria-pressed={card.isStarred}
                        className={`inline-flex h-8 w-8 max-md:h-11 max-md:w-11 items-center justify-center rounded-lg transition-colors ${
                          card.isStarred
                            ? "text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                            : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                        }`}
                      >
                        <svg viewBox="0 0 20 20" className="h-4 w-4" fill={card.isStarred ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5">
                          <path d="M10 2.5l2.2 4.45 4.9.71-3.55 3.46.84 4.88L10 13.7l-4.39 2.3.84-4.88L2.9 7.66l4.9-.71L10 2.5z" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* On a phone the row reads in lines: checkbox, star, sender and date;
                      then the subject; then the badges. The wrapper dissolves there
                      (contents), so sender and subject are placed with `order`. */}
                  <div className="min-w-0 flex-col gap-0.5 max-sm:contents sm:flex sm:w-auto sm:flex-1 sm:flex-row sm:items-center sm:gap-3">
                    <div className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100 max-sm:order-3 max-sm:min-w-0 max-sm:flex-1 sm:w-52 sm:shrink-0">
                      {sender}
                    </div>

                    <div className="flex min-w-0 flex-1 items-baseline gap-2 max-sm:order-5 max-sm:basis-full">
                      <span
                        className={`text-sm text-zinc-900 dark:text-zinc-100 truncate flex items-center gap-1.5 ${
                          showReplied ? "font-semibold" : "font-medium"
                        }`}
                      >
                        {card.draftPayload && (
                          <span className="text-red-600 dark:text-red-400 font-semibold">{t("desk_draft")}</span>
                        )}
                        {card.title}
                      </span>
                      {card.preview && (
                        <span className="hidden text-xs text-zinc-500 dark:text-zinc-500 truncate flex-1 sm:inline">
                          — {card.preview}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 max-sm:order-6 max-sm:w-full">
                    {/* The one signal the list exists for: the ball is back in
                        our court on a conversation we already answered. */}
                    {showReplied && (
                      <Badge color="emerald" className="rounded px-1.5 py-0.5 text-[10px] font-semibold">
                        {t("desk_client_replied")}
                      </Badge>
                    )}
                    <Badge color={STATUS_COLOR[card.status] || "zinc"} className="rounded px-1.5 py-0.5 text-[10px] font-semibold">
                      {t(`desk_status_${card.status}`)}
                    </Badge>
                    {card.isNoise && (
                      <Badge color="zinc" className="rounded px-1.5 py-0.5 text-[10px] font-semibold">
                        {t("desk_noise")}
                      </Badge>
                    )}
                    {card.isManual && (
                      <Badge color="indigo" className="rounded px-1.5 py-0.5 text-[10px] font-semibold">
                        {t("desk_badge_manual")}
                      </Badge>
                    )}
                    {card.isSharedWithMe ? (
                      <Badge color="violet" className="rounded px-1.5 py-0.5 text-[10px] font-semibold">
                        {t("desk_shared_with_me")}
                      </Badge>
                    ) : card.isShared ? (
                      <Badge color="violet" className="rounded px-1.5 py-0.5 text-[10px] font-semibold">
                        {t("desk_shared")}
                      </Badge>
                    ) : null}
                    {!isArchive && card.waitingSince && (
                      <Badge color="amber" title={t("desk_waiting")} className="rounded px-1.5 py-0.5 text-[10px] font-semibold">
                        {waitingAge(card.waitingSince)}
                      </Badge>
                    )}
                  </div>

                  <div
                    className="ml-auto w-20 text-right text-xs text-zinc-500 dark:text-zinc-500 whitespace-nowrap max-sm:order-4 sm:ml-0 sm:w-24"
                    title={formatPrettyDate(shownAt, dateLocale, true)}
                  >
                    {formatDeskTimestamp(shownAt, dateLocale)}
                  </div>

                  {/* Hidden on a phone: the same actions are in the open card, and the
                      bulk bar covers the rest. */}
                  <div className="flex min-w-[7.5rem] shrink-0 items-center justify-end gap-1.5 max-sm:hidden" onClick={(e) => e.stopPropagation()}>
                    {canMutate && !isTrash && (
                      <DeskLinkCrmProjectPopover
                        companyId={companyId!}
                        variant="row"
                        linkedProject={card.crmProject || null}
                        disabled={rowBusy || !companyId}
                        onLink={async (projectId) => {
                          if (!companyId) return;
                          setBusyCard(card.number);
                          try {
                            await updateDeskCard(companyId, card.number, {
                              crmProject: projectId,
                            });
                            await load();
                            toast.success(
                              t("desk_crm_project_linked", {
                                defaultValue: "Deal linked.",
                              })
                            );
                          } catch (err: any) {
                            toast.error(
                              err?.response?.data?.message ||
                                err?.message ||
                                t("error")
                            );
                          } finally {
                            setBusyCard(null);
                          }
                        }}
                        onUnlink={async () => {
                          if (!companyId) return;
                          setBusyCard(card.number);
                          try {
                            await updateDeskCard(companyId, card.number, {
                              crmProject: null,
                            });
                            setCards((prev) =>
                              prev.map((c) =>
                                c.number === card.number
                                  ? { ...c, crmProject: null }
                                  : c
                              )
                            );
                            toast.success(
                              t("desk_crm_project_unlinked", {
                                defaultValue: "Deal unlinked.",
                              })
                            );
                          } catch (err: any) {
                            toast.error(
                              err?.response?.data?.message ||
                                err?.message ||
                                t("error")
                            );
                          } finally {
                            setBusyCard(null);
                          }
                        }}
                      />
                    )}
                    {card.isDraft && !isTrash && canMutate && (
                      <button
                        type="button"
                        disabled={rowBusy}
                        onClick={async () => {
                          if (!companyId) return;
                          setBusyCard(card.number);
                          try {
                            await updateDeskCard(companyId, card.number, { isDraft: false });
                            await load();
                            toast.success(t("desk_publish_success", { number: card.number }));
                          } catch (err: any) {
                            toast.error(err?.response?.data?.message || err?.message || t("error"));
                          } finally {
                            setBusyCard(null);
                          }
                        }}
                        className={`${REVEAL_ON_HOVER_CLS} inline-flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-950/30 dark:hover:text-emerald-400 transition-opacity`}
                        title={t("desk_publish")}
                        aria-label={t("desk_publish")}
                      >
                        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
                          <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                        </svg>
                      </button>
                    )}
                    {isTrash && canMutate && (
                      <button
                        type="button"
                        disabled={rowBusy}
                        onClick={() => openRestoreConfirm(card.number, card.title, card.isDraft)}
                        className={`${REVEAL_ON_HOVER_CLS} inline-flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-950/30 dark:hover:text-indigo-400 transition-opacity`}
                        title={t("desk_restore")}
                        aria-label={t("desk_restore")}
                      >
                        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                    )}
                    {canMutate && (
                    <button
                      type="button"
                      disabled={rowBusy}
                      onClick={() => openRowDelete(card.number, card.title)}
                      className={`${REVEAL_ON_HOVER_CLS} inline-flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400 transition-opacity`}
                      title={t(isTrash ? "desk_delete_permanently" : "desk_confirm_delete_card_title")}
                      aria-label={t(isTrash ? "desk_delete_permanently" : "desk_confirm_delete_card_title")}
                    >
                      <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
                        <path fillRule="evenodd" d="M7.5 3a1 1 0 011-1h3a1 1 0 011 1V4h3.5a.75.75 0 010 1.5h-.69l-.7 9.1A2 2 0 0112.62 16.5H7.38a2 2 0 01-1.99-1.9l-.7-9.1H4a.75.75 0 010-1.5H7.5V3zm1.5.5v.5h2v-.5h-2zm-2.06 2.5l.66 8.5h5.8l.66-8.5H6.94z" clipRule="evenodd" />
                      </svg>
                    </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-2 text-sm md:justify-end">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label={t("desk_page_prev")}
            className="rounded-lg border border-zinc-200 px-3.5 py-1.5 transition-colors hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-800 dark:hover:bg-zinc-900 max-md:min-h-11 max-md:min-w-20"
          >
            ←
          </button>
          <span className="px-1 font-medium text-zinc-500">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label={t("desk_page_next")}
            className="rounded-lg border border-zinc-200 px-3.5 py-1.5 transition-colors hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-800 dark:hover:bg-zinc-900 max-md:min-h-11 max-md:min-w-20"
          >
            →
          </button>
        </div>
      )}

      {activeCardNumber !== null && (
        <DeskCardDetailPanel
          cardNumber={activeCardNumber}
          companyId={companyId || ""}
          onClose={closeCard}
          onUpdate={load}
          onNavigate={followCard}
        />
      )}
    </div>
  );
}
